import React, { memo } from 'react';
import { format } from 'date-fns';
import { DayColumnProps } from './types';

// Add type declaration for the window object to include our custom property
declare global {
  interface Window {
    eventBeingEdited?: boolean;
  }
}

const DayColumn = memo<DayColumnProps>(({ date, events, onAddEvent, onDeleteEvent, availableRanges = [] }) => {
  const formattedDate = format(date, 'EEE, MMM d');
  const dayEvents = events.filter(event => {
    const eventDate = new Date(event.start);
    // Ensure we compare only date parts (not time)
    const sameDay = (
      date.getFullYear() === eventDate.getFullYear() &&
      date.getMonth() === eventDate.getMonth() &&
      date.getDate() === eventDate.getDate()
    );

    return sameDay;
  });

  // Height per hour in pixels
  const HOUR_HEIGHT = 44;
  const TOTAL_HEIGHT = 12 * HOUR_HEIGHT; // 12 hours (8 AM - 8 PM)
  const START_HOUR = 8; // Calendar starts at 8 AM

  // Check if staff has no availability for this day
  const isNotWorking = availableRanges.length === 0;

  // Calculate unavailable hour blocks (hours outside available ranges)
  const getUnavailableBlocks = () => {
    if (isNotWorking) {
      // Entire day is not working - return single block covering 8AM-8PM
      return [{ startHour: 8, endHour: 20 }];
    }

    // Sort available ranges by start time
    const sortedRanges = [...availableRanges].sort((a, b) => a.start.localeCompare(b.start));
    const unavailable: { startHour: number; endHour: number }[] = [];

    // Convert time string "HH:mm" to decimal hour
    const timeToHour = (time: string) => {
      const [h, m] = time.split(':').map(Number);
      return h + m / 60;
    };

    let lastEnd = 8; // Start of calendar day

    for (const range of sortedRanges) {
      const rangeStart = timeToHour(range.start);
      const rangeEnd = timeToHour(range.end);

      // Add unavailable block before this range (if there's a gap)
      if (rangeStart > lastEnd) {
        unavailable.push({ startHour: lastEnd, endHour: rangeStart });
      }
      lastEnd = Math.max(lastEnd, rangeEnd);
    }

    // Add unavailable block after last range until end of calendar day (8PM)
    if (lastEnd < 20) {
      unavailable.push({ startHour: lastEnd, endHour: 20 });
    }

    return unavailable;
  };

  const unavailableBlocks = getUnavailableBlocks();

  return (
    <div className="flex-1 border-l border-gray-200 relative" style={{ minHeight: `${TOTAL_HEIGHT + 40}px` }}>
      <div className="h-10 flex items-center justify-center border-b border-gray-200 bg-gray-50 text-sm">
        {formattedDate}
      </div>
      <div className="p-1 relative" style={{ height: `${TOTAL_HEIGHT}px` }}>
        {/* Hour grid lines */}
        {Array.from({ length: 12 }, (_, i) => i + 8).map((hour) => (
          <div
            key={hour}
            className="absolute w-full border-t border-gray-100"
            style={{ top: `${(hour - 8) * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px`, left: 0 }}
          ></div>
        ))}

        {/* Unavailable/Not working overlay blocks */}
        {unavailableBlocks.map((block, index) => {
          const top = (block.startHour - START_HOUR) * HOUR_HEIGHT;
          const height = (block.endHour - block.startHour) * HOUR_HEIGHT;
          return (
            <div
              key={`unavailable-${index}`}
              className="absolute left-0 right-0 bg-gray-100 pointer-events-none flex items-center justify-center"
              style={{
                top: `${top}px`,
                height: `${height}px`,
                zIndex: 1,
              }}
            >
              {isNotWorking && index === 0 && (
                <span className="text-gray-400 text-sm font-medium italic">Not working</span>
              )}
            </div>
          );
        })}

        {dayEvents.map(event => {
          // Calculate position and height based on event time
          const startTime = new Date(event.start);
          const endTime = new Date(event.end);
          const startHour = startTime.getHours() + startTime.getMinutes() / 60;
          const endHour = endTime.getHours() + endTime.getMinutes() / 60;

          // Position events from 8AM to 8PM
          const top = Math.max(0, (startHour - 8) * HOUR_HEIGHT);
          const height = Math.max(20, (endHour - startHour) * HOUR_HEIGHT); // at least 20px height
          
          return (
          <div 
            key={event.id}
            style={{
              position: 'absolute',
              top: `${top}px`,
              height: `${height}px`,
              left: '8px',
              right: '8px',
              zIndex: 10
            }}
            className={`p-2 text-xs rounded ${event.isBlockedTime ? 'bg-gray-200 border-2 border-dashed border-red-400 text-gray-700' : 'text-white'} ${
              // Different styling for blocked time vs regular appointments
              !event.isBlockedTime ? (
                // Color based on payment status
                event.paymentStatus === 'paid' ? 'bg-blue-600' :
                event.paymentStatus === 'pending_payment' || event.paymentStatus === 'pending' ? 'bg-green-600' :
                // Fallback to appointment status for cancelled/no-show
                event.status === 'cancelled' ? 'bg-red-500' :
                event.status === 'no_show' ? 'bg-gray-500' : 'bg-green-600'
              ) : ''
            } relative group overflow-y-auto`}
          >
            {/* Delete button - shown on hover */}
            {onDeleteEvent && (
              <button 
                className="absolute top-1 right-1 text-white bg-red-500 rounded-full h-5 w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the edit event
                  if (window.confirm('Are you sure you want to delete this event?')) {
                    onDeleteEvent(event.id);
                  }
                }}
                title="Delete event"
              >
                ×
              </button>
            )}
            
            <div
              className="cursor-pointer"
              onClick={() => {
                if (event.isBlockedTime) {
                  // For blocked times, open edit modal
                  window.eventBeingEdited = true;
                  window.dispatchEvent(new CustomEvent('edit-event', { detail: event }));
                } else {
                  // For appointments, open details modal
                  window.dispatchEvent(new CustomEvent('view-appointment-details', { detail: event }));
                }
              }}
            >
              <div className="font-medium text-xs truncate">{event.title}</div>
              <div className="text-xs">
                {format(new Date(event.start), 'h:mm a')} - {format(new Date(event.end), 'h:mm a')}
              </div>
              {event.clientPhone && <div className="text-xs truncate">{event.clientPhone}</div>}
              {event.isBlockedTime && <div className="text-red-500 font-medium text-xs mt-1">BLOCKED TIME</div>}
              {!event.isBlockedTime && event.paymentStatus && (
                <div className="mt-1">
                  {event.paymentStatus === 'paid' && (
                    <span className="inline-block px-1.5 py-0.5 bg-green-200 text-green-900 text-xs font-semibold rounded">Paid</span>
                  )}
                  {event.paymentStatus === 'pending_payment' && (
                    <span className="inline-block px-1.5 py-0.5 bg-yellow-200 text-yellow-900 text-xs font-semibold rounded">Pending</span>
                  )}
                  {event.paymentStatus === 'failed' && (
                    <span className="inline-block px-1.5 py-0.5 bg-red-200 text-red-900 text-xs font-semibold rounded">Failed</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )})}
        
        <button 
          className="absolute bottom-4 left-0 right-0 mx-2 p-1 text-xs border border-dashed border-gray-300 rounded hover:border-gray-400 text-gray-500"
          onClick={() => onAddEvent(date)}
        >
          + Add
        </button>
      </div>
    </div>
  );
});

export default DayColumn;